/**
 * After PATCH /orders-update succeeds: TaxCloud version call (refund path), project link,
 * optional edit emails/notifications, then caller navigates + toasts.
 */
import type { MutableRefObject } from "react";
import { linkProjectQuote, unlinkProjectQuote } from "components/proejctsandquoteapicalls";
import { sendSalesOrderTriggerEmail } from "utils/salesOrderTriggerEmailEngine";
import { safeConsole } from "utils/envUtils";
import { showError, showSuccess } from "toast/toast";
import type { Order } from "types";
import type { SignageEditOrderBuyerContext } from "components/SignageEditOrder/buildSignageEditOrderUpdatePayload";

export type SignageEditOrderAfterSuccessfulPatchArgs = {
  response: Response;
  data: any;
  refundSuccess: boolean;
  token: string | null | undefined;
  order: Order | null;
  orderId: string;
  orderIds: string;
  values: any;
  selectedProducts: any[];
  shippingCost: number;
  tax: number;
  userClearedProject: boolean;
  linkedProjectId: string | null;
  projectIdFromQuery: string;
  isSignageTeammember: boolean;
  parentSignageId: string | null;
  parentSignageName: string | null;
  userId: string | null | undefined;
  userName: string | null | undefined;
  baseRoute: string;
  selectedOfficeAgent: any;
  selectedAgent: any;
  effectiveCouponCreatedById?: string | null | undefined;
  initialFormValuesRef: MutableRefObject<any>;
  buyer: SignageEditOrderBuyerContext;
  safeNavigate: (path: string) => void;
};

/**
 * @returns true if the happy path ran (navigate + success toast should follow); false if early exit (e.g. TaxCloud failure).
 */
export async function runSignageEditOrderAfterSuccessfulPatch(
  args: SignageEditOrderAfterSuccessfulPatchArgs
): Promise<boolean> {
  const {
    response,
    data,
    refundSuccess,
    token,
    order,
    orderId,
    orderIds,
    values,
    selectedProducts,
    shippingCost,
    tax,
    userClearedProject,
    linkedProjectId,
    projectIdFromQuery,
    isSignageTeammember,
    parentSignageId,
    parentSignageName,
    userId,
    userName,
    baseRoute,
    selectedOfficeAgent,
    selectedAgent,
    initialFormValuesRef,
    buyer,
    safeNavigate,
  } = args;

  const { agent_name } = buyer;

  if (response.status === 200 && refundSuccess) {
    try {
      if (!token) {
        showError("Missing session token while attempting TaxCloud refund.");
        return false;
      }
      await fetch(`${process.env.REACT_APP_API_URL}/taxcloud_order/version`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ order_type: "sales", order_number: order?.order_number }),
      });
    } catch (err) {
      safeConsole.error("[INSTALL:ORDER_SUBMIT] PostVersionOrdernumber failed", err);
      showError("TaxCloud refund call failed. Please try again.");
      return false;
    }
  }

  if (!response.ok) {
    showError(data.error);
    return false;
  }

  const effectiveProjectIdForLink = userClearedProject
    ? ""
    : String(linkedProjectId || projectIdFromQuery || "").trim();
  if (userClearedProject && token) {
    // Only unlink the project that was already saved on this order.
    const projectIdToUnlink = String((order as any)?.project_id || "").trim();
    if (projectIdToUnlink && projectIdToUnlink !== "[object Object]") {
      try {
        await unlinkProjectQuote(token, projectIdToUnlink);
      } catch (err) {
        safeConsole.warn("Failed to unlink sales order from project:", err);
      }
    }
  } else if (effectiveProjectIdForLink && effectiveProjectIdForLink !== "[object Object]" && token) {
    try {
      const linkedOrderId = String(data?.id || data?._id || orderId || "").trim();
      const linkedOrderNumber = String(data?.order_number || order?.order_number || linkedOrderId).trim();
      if (linkedOrderId) {
        await linkProjectQuote(token, effectiveProjectIdForLink, {
          linked_order_id: linkedOrderId,
          linked_order_number: linkedOrderNumber || linkedOrderId,
          linked_order_type: "sales_order",
        });
      }
    } catch (err) {
      safeConsole.warn("Failed to link edited sales order to project:", err);
    }
  }

  const initialValues = initialFormValuesRef.current;
  const hasChanges =
    initialValues &&
    (values.userType !== initialValues.userType ||
      values.brokerageFirmIddd !== initialValues.brokerageFirmId ||
      values.teamMemberId !== initialValues.teamMemberId ||
      values.officeIddd !== initialValues.officeId ||
      values.officeAgentId !== initialValues.agentId ||
      values.userFirstName !== initialValues.customerFirstName ||
      values.userLastName !== initialValues.customerLastName ||
      values.userEmail !== initialValues.customerEmail ||
      values.userPhone !== initialValues.customerPhone ||
      values.billingAddressId !== initialValues.billingAddressId ||
      values.shippingAddressId !== initialValues.shippingAddressId ||
      values.notes !== initialValues.notes ||
      JSON.stringify(selectedProducts) !== initialValues.items ||
      shippingCost !== initialValues.shippingAmount ||
      tax !== initialValues.taxAmount);

  if (hasChanges) {
    try {
      const from = process.env.REACT_APP_FROM_EMAIL2;
      if (from && token) {
        const orderNumber = order?.order_number || orderIds || `${order?.id || "ORD-XXXX"}`;
        const actualOrderId = String(order?.id || orderId || "").trim();
        const signageId = String(order?.signage_id || parentSignageId || userId || "").trim();
        const signageCompanyName =
          isSignageTeammember && parentSignageName ? parentSignageName : userName || "Signage Company";
        const signageAdminName = userName || signageCompanyName || "Signage Admin";
        const updateDate = new Date().toLocaleDateString("en-US", {
          month: "2-digit",
          day: "2-digit",
          year: "numeric",
        });

        const changesList: string[] = [];
        if (
          values.userFirstName !== initialValues.customerFirstName ||
          values.userLastName !== initialValues.customerLastName
        ) {
          changesList.push("Customer name updated");
        }
        if (values.userEmail !== initialValues.customerEmail) {
          changesList.push("Email updated");
        }
        if (
          values.billingAddressId !== initialValues.billingAddressId ||
          values.shippingAddressId !== initialValues.shippingAddressId
        ) {
          changesList.push("Address updated");
        }
        if (JSON.stringify(selectedProducts) !== initialValues.items) {
          changesList.push("Products updated");
        }
        if (values.notes !== initialValues.notes) {
          changesList.push("Notes updated");
        }
        if (shippingCost !== initialValues.shippingAmount) {
          changesList.push("Shipping updated");
        }
        const changesMade = changesList.length > 0 ? changesList.join(", ") : "Order details updated";

        const agentName = agent_name || selectedOfficeAgent?.label || selectedAgent?.label || "N/A";

        const viewOrderUrl = `${window.location.origin}${baseRoute}/orderDetails/${order?.id}`;

        if (actualOrderId && signageId) {
          await sendSalesOrderTriggerEmail({
            triggerName: "Signage Sales order edit",
            orderId: actualOrderId,
            signageId,
            token,
            fromEmail: from,
            fallbackCompanyName: signageCompanyName,
            fallbackUserName: userName || undefined,
            extraParams: {
              "Order ID": `#${orderNumber}`,
              "Order Number": `#${orderNumber}`,
              "Signage Company Name": signageCompanyName,
              "Signage Admin Name": signageAdminName,
              "Update Date": updateDate,
              "Updated On": updateDate,
              "Changes Made": changesMade,
              "Agent Name": agentName,
              "View Updated Order": viewOrderUrl,
              "View My Orders": `${window.location.origin}/agent/orders`,
            },
          });
        }
      }
    } catch (emailError) {
      safeConsole.warn("Failed to send order edit emails:", emailError);
    }
  }

  safeNavigate(`${baseRoute}/orderDetails/${order?.id}`);
  showSuccess("Order updated successfully!");
  return true;
}
